import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'bitte-fixed bitte-inset-0 bitte-z-50 bitte-bg-black/80 bitte- data-[state=open]:bitte-animate-in data-[state=closed]:bitte-animate-out data-[state=closed]:bitte-fade-out-0 data-[state=open]:bitte-fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'bitte-fixed bitte-left-[50%] bitte-top-[50%] bitte-z-50 bitte-grid bitte-w-full bitte-max-w-lg bitte-translate-x-[-50%] bitte-translate-y-[-50%] bitte-gap-4 bitte-border bitte-bg-background bitte-p-6 bitte-shadow-lg bitte-duration-200 data-[state=open]:bitte-animate-in data-[state=closed]:bitte-animate-out data-[state=closed]:bitte-fade-out-0 data-[state=open]:bitte-fade-in-0 data-[state=closed]:bitte-zoom-out-95 data-[state=open]:bitte-zoom-in-95 data-[state=closed]:bitte-slide-out-to-left-1/2 data-[state=closed]:bitte-slide-out-to-top-[48%] data-[state=open]:bitte-slide-in-from-left-1/2 data-[state=open]:bitte-slide-in-from-top-[48%] sm:bitte-rounded-lg',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="bitte-absolute bitte-right-4 bitte-top-4 bitte-rounded-sm bitte-opacity-70 bitte-ring-offset-background bitte-transition-opacity hover:bitte-opacity-100 focus:bitte-outline-none focus:bitte-ring-2 focus:bitte-ring-ring focus:bitte-ring-offset-2 disabled:bitte-pointer-events-none data-[state=open]:bitte-bg-accent data-[state=open]:bitte-text-muted-foreground">
        <X className="bitte-h-4 bitte-w-4" />
        <span className="bitte-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'bitte-flex bitte-flex-col bitte-space-y-1.5 bitte-text-center sm:bitte-text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'bitte-flex bitte-flex-col-reverse sm:bitte-flex-row sm:bitte-justify-end sm:bitte-space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'bitte-text-lg bitte-font-semibold bitte-leading-none bitte-tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('bitte-text-sm bitte-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
